import React, { useMemo, useRef, useState } from 'react';
import {
  Wallet,
  ArrowUpCircle,
  ArrowDownCircle,
  Target,
  TrendingUp,
  CalendarClock,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  ArrowRight,
  AlertTriangle,
  Lightbulb,
  Calendar,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useFinanceStore, Transaction, TransactionStatus } from '../store/useFinanceStore';
import DoughnutChart from '../components/charts/DoughnutChart';
import NewGoalModal from '../components/NewGoalModal';
import GoalsCarousel, { GoalsCarouselHandle } from '../components/GoalsCarousel';
import { useTheme } from '../contexts/ThemeContext';
import { normalizeStatus } from '../utils/transactionMetrics';

const formatCurrency = (value: number) =>
  value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const clampPercentage = (value: number) => Math.min(100, Math.max(0, value));

const Dashboard = () => {
  const { transactions, goals } = useFinanceStore();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const carouselRef = useRef<GoalsCarouselHandle>(null);
  const [isNewGoalModalOpen, setIsNewGoalModalOpen] = useState(false);
  const [cycleAnchorDate, setCycleAnchorDate] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [isCycleDetailsOpen, setIsCycleDetailsOpen] = useState(false);
  const [isIncomeDetailsOpen, setIsIncomeDetailsOpen] = useState(false);
  const [isExpenseDetailsOpen, setIsExpenseDetailsOpen] = useState(false);
  const [carouselNavState, setCarouselNavState] = useState({ canGoPrev: false, canGoNext: false });

  const cycleStart = useMemo(() => {
    return new Date(cycleAnchorDate.getFullYear(), cycleAnchorDate.getMonth(), 1);
  }, [cycleAnchorDate]);

  const cycleEnd = useMemo(() => {
    return new Date(cycleAnchorDate.getFullYear(), cycleAnchorDate.getMonth() + 1, 0);
  }, [cycleAnchorDate]);

  const today = useMemo(() => new Date(), []);

  const handleCycleChange = (direction: 'prev' | 'next') => {
    setCycleAnchorDate((prev) => {
      const delta = direction === 'prev' ? -1 : 1;
      return new Date(prev.getFullYear(), prev.getMonth() + delta, 1);
    });
  };

  const handleCarouselPrev = () => {
    carouselRef.current?.goToPrev();
  };

  const handleCarouselNext = () => {
    carouselRef.current?.goToNext();
  };

  const metrics = useMemo(() => {
    const now = new Date();

    const accumulator = {
      confirmedBalance: 0,
      forecastBalance: 0,
      confirmedIncome: 0,
      forecastIncome: 0,
      confirmedExpenses: 0,
      forecastExpenses: 0,
      expenseBreakdown: {
        onTime: 0,
        confirmed: 0,
        paid: 0,
        overdue: 0,
      },
      upcomingTransactions: 0,
      projectedEndBalance: 0,
      netCashflow: 0,
    };

    transactions.forEach((transaction) => {
      const amount = transaction.amount;
      const absAmount = Math.abs(amount);
      const txDate = new Date(transaction.date);
      const isDateValid = !Number.isNaN(txDate.getTime());

      if (!isDateValid) {
        return;
      }

      const isFutureRelativeToToday = txDate > now;
      const isWithinCycle = txDate >= cycleStart && txDate <= cycleEnd;

      if (!isWithinCycle) {
        return;
      }

      const status = normalizeStatus(transaction, now);

      if (amount > 0) {
        if (status === 'confirmed' || status === 'paid') {
          accumulator.confirmedIncome += amount;
          accumulator.confirmedBalance += amount;
        } else if (status === 'scheduled' || status === 'overdue') {
          accumulator.forecastIncome += amount;
          accumulator.forecastBalance += amount;
          if (isFutureRelativeToToday) {
            accumulator.upcomingTransactions += 1;
          }
        }
      } else if (amount < 0) {
        if (status === 'confirmed' || status === 'paid') {
          accumulator.confirmedExpenses += absAmount;
          accumulator.confirmedBalance += amount;
          if (status === 'paid') {
            accumulator.expenseBreakdown.paid += absAmount;
          } else {
            accumulator.expenseBreakdown.confirmed += absAmount;
          }
        } else if (status === 'scheduled') {
          accumulator.forecastExpenses += absAmount;
          accumulator.forecastBalance += amount;
          accumulator.expenseBreakdown.onTime += absAmount;
          if (isFutureRelativeToToday) {
            accumulator.upcomingTransactions += 1;
          }
        } else if (status === 'overdue') {
          accumulator.forecastExpenses += absAmount;
          accumulator.forecastBalance += amount;
          accumulator.expenseBreakdown.overdue += absAmount;
        }
      }
    });

    accumulator.projectedEndBalance = accumulator.confirmedBalance + accumulator.forecastBalance;
    accumulator.netCashflow =
      accumulator.confirmedIncome +
      accumulator.forecastIncome -
      (accumulator.confirmedExpenses + accumulator.forecastExpenses);

    return accumulator;
  }, [transactions, cycleStart, cycleEnd, today]);

  const goalSummary = useMemo(() => {
    const activeGoals = goals.filter((g) => !g.completed);
    const totalSaved = activeGoals.reduce((sum, g) => sum + g.currentAmount, 0);
    const totalTarget = activeGoals.reduce((sum, g) => sum + g.targetAmount, 0);
    const progress = totalTarget > 0 ? totalSaved / totalTarget : 0;

    return {
      totalActive: activeGoals.length,
      totalSaved,
      totalTarget,
      progress,
    };
  }, [goals]);

  const expenseBreakdownEntries = useMemo(() => {
    const entries = [
      {
        label: 'No prazo',
        value: metrics.expenseBreakdown.onTime,
        color: 'text-sky-300',
      },
      {
        label: 'Confirmado',
        value: metrics.expenseBreakdown.confirmed,
        color: 'text-emerald-300',
      },
      {
        label: 'Pago',
        value: metrics.expenseBreakdown.paid,
        color: 'text-emerald-200',
      },
      {
        label: 'Atrasado',
        value: metrics.expenseBreakdown.overdue,
        color: 'text-rose-300',
      },
    ];

    const total = entries.reduce((sum, entry) => sum + entry.value, 0);

    return entries.map((entry) => ({
      ...entry,
      percentage: total > 0 ? (entry.value / total) * 100 : 0,
    }));
  }, [metrics.expenseBreakdown]);

  const totalMonthlyIncome = metrics.confirmedIncome + metrics.forecastIncome;
  const totalMonthlyExpenses = metrics.confirmedExpenses + metrics.forecastExpenses;
  const projectedCycleBalance = metrics.projectedEndBalance;
  const monthlyNetFlow = metrics.netCashflow;
  const projectedAmountLabel = formatCurrency(projectedCycleBalance);
  const projectedAmountAbsLabel = formatCurrency(Math.abs(projectedCycleBalance));

  const cycleLabel = cycleStart.toLocaleDateString('pt-BR', {
    month: 'long',
    year: 'numeric',
  });
  const cycleStartLabel = cycleStart.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
  });
  const cycleEndLabel = cycleEnd.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
  });
  const cycleEndLabelExtended = cycleEnd.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'long',
  });
  const nextCycleStart = new Date(cycleEnd.getFullYear(), cycleEnd.getMonth() + 1, 1);
  const nextCycleStartLabel = nextCycleStart.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'long',
  });
  const cycleRangeLabel = `${cycleStartLabel} — ${cycleEndLabel}`;

  const cycleTotalDays = cycleEnd.getDate();
  const daysElapsed = (() => {
    if (today < cycleStart) return 0;
    if (today > cycleEnd) return cycleTotalDays;
    const diff = today.getTime() - cycleStart.getTime();
    return Math.floor(diff / (1000 * 60 * 60 * 24)) + 1;
  })();
  const remainingDays = Math.max(0, cycleTotalDays - daysElapsed);
  const cycleProgressPercent = cycleTotalDays > 0 ? (daysElapsed / cycleTotalDays) * 100 : 0;

  const insights: string[] = [];

  if (projectedCycleBalance < 0) {
    insights.push(
      `Projeção indica encerrar o ciclo com saldo negativo de ${projectedAmountAbsLabel}. Ajuste gastos ou antecipe receitas para evitar o vermelho.`
    );
  } else {
    insights.push(
      `Mantendo o ritmo atual, o ciclo pode terminar com ${projectedAmountLabel} disponíveis para novas decisões.`
    );
  }

  if (metrics.forecastBalance < 0) {
    insights.push(
      `Despesas futuras comprometem ${formatCurrency(Math.abs(metrics.forecastBalance))}; prepare cobertura antes de ${cycleEndLabelExtended}.`
    );
  } else if (metrics.forecastBalance > 0) {
    insights.push(
      `Há ${formatCurrency(metrics.forecastBalance)} de entradas previstas aguardando confirmação; acompanhe para reforçar o caixa.`
    );
  }

  if (monthlyNetFlow > 0 && projectedCycleBalance > 0) {
    const reserveSuggestion = Math.min(projectedCycleBalance, monthlyNetFlow);
    insights.push(
      `Separe até ${formatCurrency(reserveSuggestion)} para metas ou investimentos sem comprometer o fluxo do ciclo.`
    );
  } else if (monthlyNetFlow < 0) {
    insights.push(
      `Fluxo líquido negativo em ${formatCurrency(Math.abs(monthlyNetFlow))}; reduza despesas variáveis nas próximas semanas.`
    );
  }

  if (!insights.length) {
    insights.push('Ciclo equilibrado. Continue acompanhando as confirmações para manter o saldo positivo.');
  }

  return (
    <>
      <div className="space-y-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div
            className={`rounded-2xl p-6 shadow-xl transition-colors ${
              isDark
                ? 'bg-slate-900/80 border border-slate-700/60'
                : 'bg-gradient-to-r from-white via-teal-50 to-cyan-50 border border-teal-200 shadow-lg shadow-teal-100/50'
            }`}
          >
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div>
                <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  Minha Gestão Financeira
                </h2>
                <p className={`text-sm mt-1 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                  Visão consolidada do seu fluxo de caixa, lançamentos e metas em tempo real.
                </p>
              </div>
              <div
                className={`flex items-center gap-3 text-sm ${
                  isDark ? 'text-slate-300' : 'text-teal-700'
                }`}
              >
                <CalendarClock size={18} className={isDark ? 'text-emerald-300' : 'text-teal-500'} />
                <span className={isDark ? '' : 'font-medium'}>
                  Hoje é{' '}
                  {today.toLocaleDateString('pt-BR', {
                    weekday: 'long',
                    day: '2-digit',
                    month: 'long',
                  })}
                </span>
              </div>
            </div>
          </div>

          <div className="mt-8 space-y-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.1 }}
              whileHover={{ y: -4 }}
              className={`rounded-2xl p-6 shadow-2xl transition-colors ${
                isDark
                  ? 'bg-gradient-to-br from-blue-900/70 via-slate-900/80 to-slate-900/90 border border-blue-500/40'
                  : 'bg-gradient-to-br from-white via-cyan-50 to-teal-50 border border-teal-200 shadow-lg shadow-teal-100/40'
              }`}
            >
              <div className="flex flex-col gap-6">
                {/* Saldo Projetado - SEMPRE VISÍVEL */}
                <div
                  className={`flex flex-wrap items-start justify-between gap-4 rounded-2xl border p-5 transition-colors ${
                    isDark
                      ? 'border-blue-500/30 bg-blue-900/40'
                      : 'border-teal-200 bg-white/80 shadow-inner shadow-teal-100/30'
                  }`}
                >
                  <div className="space-y-4">
                    <div>
                      <p
                        className={`text-sm font-semibold uppercase tracking-[0.12em] ${
                          isDark ? 'text-blue-200/80' : 'text-teal-700'
                        }`}
                      >
                        Saldo projetado do ciclo
                      </p>
                      <div className="mt-2 flex flex-wrap items-end gap-3">
                        <p
                          className={`text-4xl font-bold leading-tight ${
                            projectedCycleBalance < 0
                              ? isDark
                                ? 'text-rose-200'
                                : 'text-rose-500'
                              : isDark
                                ? 'text-white'
                                : 'text-teal-700'
                          }`}
                        >
                          {projectedAmountLabel}
                        </p>
                        <span className={`text-xs ${isDark ? 'text-blue-100/70' : 'text-teal-600'}`}>
                          até {cycleEndLabelExtended}
                        </span>
                      </div>
                      <p className={`mt-2 text-sm ${isDark ? 'text-blue-100/80' : 'text-slate-600'}`}>
                        {projectedCycleBalance < 0
                          ? 'Com os lançamentos previstos você tende a encerrar o ciclo no negativo.'
                          : 'Mantendo o ritmo atual você encerra o ciclo no positivo.'}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      {projectedCycleBalance < 0 ? (
                        <span
                          className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold ${
                            isDark
                              ? 'border border-rose-500/30 bg-rose-500/10 text-rose-200'
                              : 'border border-rose-200 bg-rose-50 text-rose-600'
                          }`}
                        >
                          <AlertTriangle size={12} /> Risco de saldo negativo
                        </span>
                      ) : (
                        <span
                          className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold ${
                            isDark
                              ? 'border border-emerald-500/30 bg-emerald-500/10 text-emerald-200'
                              : 'border border-emerald-200 bg-emerald-50 text-emerald-600'
                          }`}
                        >
                          <TrendingUp size={12} className={isDark ? 'text-emerald-200' : 'text-emerald-500'} /> Saldo sob controle
                        </span>
                      )}
                      <span className={`text-xs ${isDark ? 'text-blue-200/60' : 'text-slate-500'}`}>
                        Atualização em tempo real com agenda e lançamentos confirmados.
                      </span>
                    </div>
                  </div>
                  <div
                    className={`rounded-xl p-3 ${
                      isDark ? 'bg-blue-500/20' : 'bg-gradient-to-br from-teal-100 to-teal-200'
                    }`}
                  >
                    <Wallet size={26} className={isDark ? 'text-blue-200' : 'text-teal-700'} />
                  </div>
                </div>

                {/* Barra de Progresso - SEMPRE VISÍVEL */}
                <div className="space-y-4">
                  <div>
                    <div
                      className={`flex items-center justify-between text-xs ${
                        isDark ? 'text-blue-200/70' : 'text-slate-600'
                      }`}
                    >
                      <span>{`Progresso do ciclo (${cycleTotalDays} dias)`}</span>
                      <span>
                        {daysElapsed} / {cycleTotalDays} dias
                      </span>
                    </div>
                    <div
                      className={`mt-2 h-2 w-full overflow-hidden rounded-full ${
                        isDark ? 'bg-blue-500/20' : 'bg-teal-100'
                      }`}
                    >
                      <div
                        className={`h-full ${
                          isDark
                            ? 'bg-gradient-to-r from-emerald-400 via-blue-400 to-amber-300'
                            : 'bg-gradient-to-r from-teal-400 via-cyan-400 to-emerald-400'
                        }`}
                        style={{ width: `${cycleProgressPercent}%` }}
                      />
                    </div>
                  </div>

                  {/* Botão Expansível */}
                  <button
                    onClick={() => setIsCycleDetailsOpen((prev) => !prev)}
                    className={`inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
                      isDark
                        ? 'border border-blue-500/30 bg-blue-500/10 text-blue-100 hover:bg-blue-500/20'
                        : 'border border-teal-200 bg-white text-teal-600 hover:bg-teal-50'
                    }`}
                    aria-expanded={isCycleDetailsOpen}
                  >
                    {isCycleDetailsOpen ? (
                      <>
                        <ChevronUp size={14} />
                        Recolher detalhes
                      </>
                    ) : (
                      <>
                        <ChevronDown size={14} />
                        Ver detalhes do ciclo
                      </>
                    )}
                  </button>

                  {/* Conteúdo Expansível */}
                  <AnimatePresence initial={false}>
                    {isCycleDetailsOpen && (
                      <motion.div
                        key="cycle-details"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.25 }}
                        className="space-y-4 overflow-hidden"
                      >
                        {/* Cabeçalho do ciclo */}
                        <div className="flex flex-wrap items-start justify-between gap-4">
                          <div>
                            <p
                              className={`text-xs uppercase tracking-[0.2em] ${
                                isDark ? 'text-blue-200/70' : 'text-cyan-600/80'
                              }`}
                            >
                              Ciclo mensal padrão · reinicia dia 1º
                            </p>
                            <div className="mt-2 flex flex-wrap items-baseline gap-3">
                              <h3
                                className={`text-lg font-semibold capitalize ${
                                  isDark ? 'text-white' : 'text-slate-900'
                                }`}
                              >
                                Ciclo {cycleLabel}
                              </h3>
                              <span className={`text-xs ${isDark ? 'text-blue-100/70' : 'text-cyan-600/80'}`}>
                                {cycleRangeLabel}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleCycleChange('prev')}
                              className={`rounded-lg p-2 transition-colors ${
                                isDark
                                  ? 'border border-blue-500/30 bg-blue-500/10 text-blue-100 hover:bg-blue-500/20'
                                  : 'border border-teal-200 bg-white text-teal-600 hover:bg-teal-50'
                              }`}
                              aria-label="Ciclo anterior"
                            >
                              <ChevronLeft size={18} />
                            </button>
                            <button
                              onClick={() => handleCycleChange('next')}
                              className={`rounded-lg p-2 transition-colors ${
                                isDark
                                  ? 'border border-blue-500/30 bg-blue-500/10 text-blue-100 hover:bg-blue-500/20'
                                  : 'border border-teal-200 bg-white text-teal-600 hover:bg-teal-50'
                              }`}
                              aria-label="Próximo ciclo"
                            >
                              <ChevronRight size={18} />
                            </button>
                          </div>
                        </div>

                        {/* Card: O que você vê aqui */}
                        <div
                          className={`rounded-xl border p-4 ${
                            isDark
                              ? 'border-blue-500/20 bg-gradient-to-br from-blue-900/30 to-slate-900/40'
                              : 'border-teal-200 bg-gradient-to-br from-teal-50 to-white shadow-sm'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <div
                              className={`rounded-lg p-2 ${
                                isDark ? 'bg-blue-500/20' : 'bg-teal-100'
                              }`}
                            >
                              <Calendar className={isDark ? 'text-blue-200' : 'text-teal-600'} size={20} />
                            </div>
                            <div className="flex-1">
                              <p className={`text-sm font-semibold ${isDark ? 'text-blue-100' : 'text-teal-700'}`}>
                                O que você vê aqui
                              </p>
                              <p className={`mt-1.5 text-xs leading-relaxed ${isDark ? 'text-blue-200/80' : 'text-slate-700'}`}>
                                Este ciclo integra <span className="font-semibold">lançamentos confirmados</span> (dinheiro já recebido/quitado), <span className="font-semibold">recorrentes</span> (transações repetidas) e <span className="font-semibold">previstos</span> (eventos futuros) para entregar um espelho completo do mês.
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Informação sobre dias restantes */}
                        <div
                          className={`rounded-xl border p-4 ${
                            isDark
                              ? 'border-blue-500/20 bg-gradient-to-br from-blue-900/30 to-slate-900/40'
                              : 'border-teal-200 bg-gradient-to-br from-teal-50 to-white shadow-sm'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <div
                              className={`rounded-lg p-2 ${
                                isDark ? 'bg-blue-500/20' : 'bg-teal-100'
                              }`}
                            >
                              <Calendar className={isDark ? 'text-blue-200' : 'text-teal-600'} size={20} />
                            </div>
                            <div className="flex-1">
                              <p className={`text-sm font-semibold ${isDark ? 'text-blue-100' : 'text-teal-700'}`}>
                                Período do Ciclo
                              </p>
                              <p className={`mt-1 text-xs ${isDark ? 'text-blue-200/70' : 'text-slate-600'}`}>
                                {remainingDays > 0
                                  ? `Faltam ${remainingDays} dia${remainingDays > 1 ? 's' : ''} para encerrar o ciclo em ${cycleEndLabelExtended}.`
                                  : `Ciclo encerrado. Novo período inicia em ${nextCycleStartLabel}.`}
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="grid gap-3">
                          <div
                            className={`flex flex-wrap items-center justify-between gap-4 rounded-xl border p-4 transition-colors ${
                              isDark
                                ? 'border-blue-500/20 bg-slate-900/40'
                                : 'border-teal-200 bg-white/85 shadow-inner shadow-teal-100/30'
                            }`}
                          >
                            <div>
                              <p className={`text-sm font-semibold ${isDark ? 'text-blue-100' : 'text-teal-700'}`}>
                                Confirmado
                              </p>
                              <p className={`text-xs ${isDark ? 'text-blue-200/70' : 'text-slate-600'}`}>
                                Dinheiro já recebido ou quitado dentro do ciclo.
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <ArrowRight className={isDark ? 'text-emerald-300' : 'text-emerald-500'} size={18} />
                              <span
                                className={`text-3xl font-semibold ${
                                  metrics.confirmedBalance >= 0
                                    ? isDark
                                      ? 'text-emerald-300'
                                      : 'text-emerald-600'
                                    : isDark
                                      ? 'text-rose-300'
                                      : 'text-rose-500'
                                }`}
                              >
                                {formatCurrency(metrics.confirmedBalance)}
                              </span>
                            </div>
                          </div>
                          <div
                            className={`flex flex-wrap items-center justify-between gap-4 rounded-xl border p-4 transition-colors ${
                              isDark
                                ? 'border-blue-500/20 bg-slate-900/40'
                                : 'border-teal-200 bg-white/85 shadow-inner shadow-teal-100/30'
                            }`}
                          >
                            <div className="max-w-md">
                              <p className={`text-sm font-semibold ${isDark ? 'text-blue-100' : 'text-teal-700'}`}>
                                Futuro / Previsto
                              </p>
                              <p className={`text-xs ${isDark ? 'text-blue-200/70' : 'text-slate-600'}`}>
                                {metrics.forecastBalance < 0
                                  ? `Se tudo ocorrer como previsto, seu saldo tende a ${
                                      projectedCycleBalance < 0
                                        ? `ficar negativo em ${projectedAmountAbsLabel}`
                                        : `chegar a ${projectedAmountLabel}`
                                    } até ${cycleEndLabelExtended}.`
                                  : `Entradas previstas podem levar o saldo projetado para ${
                                      projectedCycleBalance < 0
                                        ? `saldo negativo de ${projectedAmountAbsLabel}`
                                        : projectedAmountLabel
                                    } até ${cycleEndLabelExtended}.`}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <ArrowRight
                                className={
                                  metrics.forecastBalance < 0
                                    ? isDark
                                      ? 'text-amber-300'
                                      : 'text-amber-500'
                                    : isDark
                                      ? 'text-emerald-200'
                                      : 'text-emerald-500'
                                }
                                size={18}
                              />
                              <span
                                className={`text-2xl font-semibold ${
                                  metrics.forecastBalance < 0
                                    ? isDark
                                      ? 'text-amber-300'
                                      : 'text-amber-500'
                                    : isDark
                                      ? 'text-emerald-200'
                                      : 'text-emerald-600'
                                }`}
                              >
                                {formatCurrency(metrics.forecastBalance)}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="grid gap-4 xl:grid-cols-2">
                          <div
                            className={`rounded-xl border p-4 transition-colors ${
                              isDark
                                ? 'border-blue-500/20 bg-blue-900/40'
                                : 'border-teal-200 bg-white/85 shadow-inner shadow-teal-100/20'
                            }`}
                          >
                            <p
                              className={`text-xs uppercase tracking-[0.18em] ${
                                isDark ? 'text-blue-200/70' : 'text-teal-700'
                              }`}
                            >
                              Fluxo financeiro do ciclo
                            </p>
                            <div
                              className={`mt-3 space-y-2 text-sm ${
                                isDark ? 'text-blue-100/90' : 'text-slate-600'
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <span>Entradas totais</span>
                                <span className={`font-semibold ${isDark ? '' : 'text-teal-700'}`}>
                                  {formatCurrency(totalMonthlyIncome)}
                                </span>
                              </div>
                              <div className="flex items-center justify-between">
                                <span>Saídas totais</span>
                                <span className={`font-semibold ${isDark ? '' : 'text-rose-500'}`}>
                                  {formatCurrency(totalMonthlyExpenses)}
                                </span>
                              </div>
                              <div className="flex items-center justify-between">
                                <span>Fluxo líquido</span>
                                <span
                                  className={`font-semibold ${
                                    monthlyNetFlow >= 0
                                      ? isDark
                                        ? 'text-emerald-300'
                                        : 'text-emerald-600'
                                      : isDark
                                        ? 'text-rose-300'
                                        : 'text-rose-500'
                                  }`}
                                >
                                  {formatCurrency(monthlyNetFlow)}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div
                            className={`rounded-xl border p-4 transition-colors ${
                              isDark
                                ? 'border-blue-500/20 bg-slate-900/40'
                                : 'border-teal-200 bg-white/85 shadow-inner shadow-teal-100/20'
                            }`}
                          >
                            <div
                              className={`flex items-center gap-2 ${
                                isDark ? 'text-blue-100' : 'text-teal-700'
                              }`}
                            >
                              <Lightbulb size={16} className={isDark ? 'text-amber-300' : 'text-amber-500'} />
                              <p className="text-xs uppercase tracking-[0.18em]">Insights rápidos</p>
                            </div>
                            <ul
                              className={`mt-3 space-y-2 text-sm ${
                                isDark ? 'text-blue-50/90' : 'text-slate-600'
                              }`}
                            >
                              {insights.map((insight, index) => (
                                <li key={index} className="flex gap-2">
                                  <span className={isDark ? 'text-emerald-300' : 'text-teal-500'}>•</span>
                                  <span>{insight}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </motion.div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.15 }}
                whileHover={{ y: -4 }}
                className={`rounded-2xl p-6 shadow-2xl transition-colors ${
                  isDark
                    ? 'bg-gradient-to-br from-emerald-900/70 to-slate-900/80 border border-emerald-500/40'
                    : 'bg-gradient-to-br from-white via-emerald-50 to-emerald-100 border border-emerald-200 shadow-lg shadow-emerald-100/40'
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 space-y-3">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p
                          className={`text-sm font-semibold tracking-[0.12em] uppercase ${
                            isDark ? 'text-emerald-200/80' : 'text-emerald-700'
                          }`}
                        >
                          Receitas do mês
                        </p>
                        <p
                          className={`text-4xl font-bold mt-2 leading-tight ${
                            isDark ? 'text-white' : 'text-emerald-700'
                          }`}
                        >
                          {formatCurrency(totalMonthlyIncome)}
                        </p>
                      </div>
                      <button
                        onClick={() => setIsIncomeDetailsOpen((prev) => !prev)}
                        className={`inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-xs font-semibold transition-colors ${
                          isDark
                            ? 'border border-emerald-500/30 bg-emerald-500/10 text-emerald-100 hover:bg-emerald-500/20'
                            : 'border border-emerald-200 bg-white text-emerald-600 hover:bg-emerald-50'
                        }`}
                        aria-expanded={isIncomeDetailsOpen}
                      >
                        {isIncomeDetailsOpen ? (
                          <>
                            <ChevronUp size={14} />
                            Fechar
                          </>
                        ) : (
                          <>
                            <ChevronDown size={14} />
                            Detalhes
                          </>
                        )}
                      </button>
                    </div>
                    <AnimatePresence initial={false}>
                      {isIncomeDetailsOpen && (
                        <motion.div
                          key="income-details"
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.2 }}
                          className="space-y-3 text-sm overflow-hidden"
                        >
                          <div
                            className={`flex items-center justify-between ${
                              isDark ? 'text-emerald-100/90' : 'text-slate-600'
                            }`}
                          >
                            <span className="font-medium">Confirmadas</span>
                            <span className={`font-semibold ${isDark ? 'text-emerald-300' : 'text-emerald-600'}`}>
                              {formatCurrency(metrics.confirmedIncome)}
                            </span>
                          </div>
                          <div
                            className={`flex items-center justify-between ${
                              isDark ? 'text-emerald-100/90' : 'text-slate-600'
                            }`}
                          >
                            <span className="font-medium">Previstas</span>
                            <span className={`font-semibold ${isDark ? 'text-amber-200' : 'text-amber-500'}`}>
                              {formatCurrency(metrics.forecastIncome)}
                            </span>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                  <div
                    className={`p-3 rounded-xl ${
                      isDark ? 'bg-emerald-500/15' : 'bg-emerald-100'
                    }`}
                  >
                    <ArrowUpCircle size={26} className={isDark ? 'text-emerald-200' : 'text-emerald-600'} />
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.2 }}
                whileHover={{ y: -4 }}
                className={`rounded-2xl p-6 shadow-2xl transition-colors ${
                  isDark
                    ? 'bg-gradient-to-br from-rose-900/70 to-slate-900/80 border border-rose-500/40'
                    : 'bg-gradient-to-br from-white via-rose-50 to-rose-100 border border-rose-200 shadow-lg shadow-rose-100/40'
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 space-y-3">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p
                          className={`text-sm font-semibold tracking-[0.12em] uppercase ${
                            isDark ? 'text-rose-200/80' : 'text-rose-600'
                          }`}
                        >
                          Saídas do mês
                        </p>
                        <p
                          className={`text-4xl font-bold mt-2 leading-tight ${
                            isDark ? 'text-white' : 'text-rose-600'
                          }`}
                        >
                          {formatCurrency(totalMonthlyExpenses)}
                        </p>
                      </div>
                      <button
                        onClick={() => setIsExpenseDetailsOpen((prev) => !prev)}
                        className={`inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-xs font-semibold transition-colors ${
                          isDark
                            ? 'border border-rose-500/30 bg-rose-500/10 text-rose-100 hover:bg-rose-500/20'
                            : 'border border-rose-200 bg-white text-rose-600 hover:bg-rose-50'
                        }`}
                        aria-expanded={isExpenseDetailsOpen}
                      >
                        {isExpenseDetailsOpen ? (
                          <>
                            <ChevronUp size={14} />
                            Fechar
                          </>
                        ) : (
                          <>
                            <ChevronDown size={14} />
                            Detalhes
                          </>
                        )}
                      </button>
                    </div>
                    <AnimatePresence initial={false}>
                      {isExpenseDetailsOpen && (
                        <motion.div
                          key="expense-details"
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.2 }}
                          className="space-y-3 text-xs sm:text-sm overflow-hidden"
                        >
                          {expenseBreakdownEntries.map((entry) => (
                            <div
                              key={entry.label}
                              className={`flex items-center justify-between ${
                                isDark ? 'text-rose-100/90' : 'text-slate-600'
                              }`}
                            >
                              <span
                                className={`${
                                  isDark
                                    ? entry.color
                                    : entry.label === 'Atrasado'
                                      ? 'text-rose-600'
                                      : entry.label === 'Confirmado'
                                        ? 'text-emerald-600'
                                        : entry.label === 'Pago'
                                          ? 'text-emerald-500'
                                          : 'text-teal-600'
                                } font-medium`}
                              >
                                {entry.label}
                              </span>
                              <span className="flex items-center gap-3">
                                <span className={`font-semibold ${isDark ? 'text-rose-200' : 'text-rose-600'}`}>
                                  {formatCurrency(entry.value)}
                                </span>
                                <span className={isDark ? 'text-rose-200/70' : 'text-slate-500'}>
                                  {clampPercentage(entry.percentage).toFixed(0)}%
                                </span>
                              </span>
                            </div>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                  <div
                    className={`p-3 rounded-xl ${
                      isDark ? 'bg-rose-500/15' : 'bg-rose-100'
                    }`}
                  >
                    <ArrowDownCircle size={26} className={isDark ? 'text-rose-200' : 'text-rose-600'} />
                  </div>
                </div>
              </motion.div>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.25 }}
            className="mt-8"
          >
            <div
              className={`rounded-2xl p-6 shadow-2xl transition-colors ${
                isDark
                  ? 'bg-gradient-to-br from-purple-900/70 to-slate-900/80 border border-purple-500/40'
                  : 'bg-gradient-to-br from-white via-purple-50 to-teal-50 border border-purple-200 shadow-lg shadow-purple-100/40'
              }`}
            >
              <div className="flex flex-col gap-6 xl:flex-row xl:items-center xl:justify-between">
                <div>
                  <p
                    className={`text-sm font-medium tracking-wide ${
                      isDark ? 'text-purple-200/80' : 'text-purple-600'
                    }`}
                  >
                    Resumo das Metas
                  </p>
                  <h3
                    className={`text-2xl font-semibold mt-1 ${
                      isDark ? 'text-white' : 'text-slate-900'
                    }`}
                  >
                    Panorama das metas ativas
                  </h3>
                  <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <p
                        className={`text-xs uppercase tracking-wide ${
                          isDark ? 'text-purple-200/70' : 'text-purple-600'
                        }`}
                      >
                        Metas ativas
                      </p>
                      <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        {goalSummary.totalActive}
                      </p>
                    </div>
                    <div>
                      <p
                        className={`text-xs uppercase tracking-wide ${
                          isDark ? 'text-purple-200/70' : 'text-purple-600'
                        }`}
                      >
                        Total poupado
                      </p>
                      <p
                        className={`text-xl font-semibold ${
                          isDark ? 'text-emerald-200' : 'text-emerald-600'
                        }`}
                      >
                        {formatCurrency(goalSummary.totalSaved)}
                      </p>
                    </div>
                    <div>
                      <p
                        className={`text-xs uppercase tracking-wide ${
                          isDark ? 'text-purple-200/70' : 'text-purple-600'
                        }`}
                      >
                        Meta total
                      </p>
                      <p
                        className={`text-xl font-semibold ${
                          isDark ? 'text-purple-100' : 'text-purple-600'
                        }`}
                      >
                        {formatCurrency(goalSummary.totalTarget)}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="w-full xl:max-w-sm">
                  <p
                    className={`text-xs uppercase tracking-wide mb-2 ${
                      isDark ? 'text-purple-200/70' : 'text-purple-600'
                    }`}
                  >
                    Progresso geral
                  </p>
                  <div
                    className={`h-2 w-full rounded-full overflow-hidden ${
                      isDark ? 'bg-white/10' : 'bg-purple-100'
                    }`}
                  >
                    <div
                      className={`h-full ${
                        isDark
                          ? 'bg-gradient-to-r from-purple-400 via-emerald-400 to-emerald-500'
                          : 'bg-gradient-to-r from-purple-400 via-teal-400 to-emerald-500'
                      }`}
                      style={{ width: `${clampPercentage(goalSummary.progress * 100)}%` }}
                    />
                  </div>
                  <div
                    className={`mt-2 flex items-center justify-between text-sm ${
                      isDark ? 'text-purple-100' : 'text-slate-600'
                    }`}
                  >
                    <span>
                      {metrics.upcomingTransactions} lançamentos futuros sincronizados na agenda
                    </span>
                    <span className={isDark ? '' : 'text-purple-600 font-semibold'}>
                      {clampPercentage(goalSummary.progress * 100).toFixed(1)}%
                    </span>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={() => setIsNewGoalModalOpen(true)}
                    className={`mt-4 inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-semibold shadow-lg transition-colors ${
                      isDark
                        ? 'bg-gradient-to-r from-emerald-500 to-emerald-400 text-slate-900 shadow-emerald-500/30'
                        : 'bg-gradient-to-r from-teal-500 to-emerald-500 text-white shadow-emerald-200/50'
                    }`}
                  >
                    <Target size={18} />
                    Criar nova meta
                  </motion.button>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.35 }}
            className="mt-10"
          >
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
              <div className="flex items-center gap-3">
                <h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  Metas em andamento
                </h2>
                <div
                  className={`flex items-center gap-2 rounded-lg border px-2 py-1 text-xs font-semibold ${
                    isDark
                      ? 'border-slate-700 bg-slate-800/80 text-slate-100'
                      : 'border-slate-200 bg-white text-slate-700'
                  }`}
                >
                  <button
                    onClick={handleCarouselPrev}
                    disabled={!carouselNavState.canGoPrev}
                    className={`rounded-md p-1 transition-colors ${
                      carouselNavState.canGoPrev
                        ? isDark
                          ? 'hover:bg-slate-700'
                          : 'hover:bg-slate-100'
                        : 'opacity-40 cursor-not-allowed'
                    }`}
                    aria-label="Meta anterior"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <span className={isDark ? 'text-slate-300' : 'text-slate-500'}>|</span>
                  <button
                    onClick={handleCarouselNext}
                    disabled={!carouselNavState.canGoNext}
                    className={`rounded-md p-1 transition-colors ${
                      carouselNavState.canGoNext
                        ? isDark
                          ? 'hover:bg-slate-700'
                          : 'hover:bg-slate-100'
                        : 'opacity-40 cursor-not-allowed'
                    }`}
                    aria-label="Próxima meta"
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
              <div
                className={`flex items-center gap-2 text-sm ${
                  isDark ? 'text-slate-400' : 'text-slate-600'
                }`}
              >
                <TrendingUp size={16} className={isDark ? 'text-emerald-300' : 'text-emerald-500'} />
                Atualizadas automaticamente após cada contribuição
              </div>
            </div>
            <GoalsCarousel
              ref={carouselRef}
              goals={goals}
              onNavStateChange={setCarouselNavState}
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.35 }}
            className="mt-12"
          >
            <motion.div
              whileHover={{ y: -4 }}
              className={`rounded-2xl border p-6 shadow-xl transition-colors ${
                isDark
                  ? 'border-slate-700/50 bg-slate-900/70'
                  : 'border-teal-200 bg-white shadow-lg shadow-teal-100/30'
              }`}
            >
              <DoughnutChart title="Lançamentos por Categoria" />
            </motion.div>
          </motion.div>
        </motion.div>
      </div>

      <NewGoalModal isOpen={isNewGoalModalOpen} onClose={() => setIsNewGoalModalOpen(false)} />
    </>
  );
};

export default Dashboard;
